// ** React Imports
import { useEffect, Fragment, useContext } from 'react'

// ** Next Import
import { useRouter } from 'next/router'

// ** MUI Imports
import Chip from '@mui/material/Chip'
import Collapse from '@mui/material/Collapse'
import ListItem from '@mui/material/ListItem'
import { styled } from '@mui/material/styles'
import Box, { BoxProps } from '@mui/material/Box'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemButton from '@mui/material/ListItemButton'
import Tooltip from '@mui/material/Tooltip'

// ** Third Party Imports
import clsx from 'clsx'

// ** Configs Import
import themeConfig from 'src/configs/themeConfig'

// ** Utils
import { hasSubActiveChild, removeChildren, resolveNavPath } from 'src/@core/layouts/utils'

// ** Type Import
import { NavGroup, LayoutProps } from 'src/@core/layouts/types'

// ** Custom Components Imports
import Translations from 'src/layouts/components/Translations'
import CanViewNavGroup from 'src/layouts/components/acl/CanViewNavGroup'
import { AbilityContext } from 'src/layouts/components/acl/Can'
import { useAuth } from '@/src/hooks/useAuth'
import { findFirstVisibleNavLinkInTree } from '@/src/layouts/bhyve-navigation-v2/navGuards'
import { NavTruncationTooltip } from '@/src/layouts/bhyve-navigation-v2/NavTruncationTooltip'
import SubVerticalSubNavItems from './SubVerticalNavItems'
import { Icon } from '@iconify/react'

interface Props {
  item: NavGroup
  navHover: boolean
  parent?: NavGroup
  navVisible?: boolean
  groupActive: string[]
  collapsedNavWidth: number
  currentActiveGroup: string[]
  navigationBorderWidth: number
  settings: LayoutProps['settings']
  isSubToSub?: NavGroup | undefined
  saveSettings: LayoutProps['saveSettings']
  setGroupActive: (values: string[]) => void
  setCurrentActiveGroup: (items: string[]) => void
  setNavHover?: (value: boolean) => void
}

const MenuItemTextWrapper = styled(Box)<BoxProps>(({ theme }) => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  justifyContent: 'space-between',
  transition: 'opacity .25s ease-in-out',
  ...(themeConfig.menuTextTruncate && { overflow: 'hidden' })
}))

const SubVerticalNavGroup = (props: Props) => {
  // ** Props
  const {
    item,
    parent,
    settings,
    navHover,
    navVisible,
    isSubToSub,
    groupActive,
    setGroupActive,
    collapsedNavWidth,
    currentActiveGroup,
    setCurrentActiveGroup,
    navigationBorderWidth,
    setNavHover
  } = props

  // ** Hooks & Vars
  const router = useRouter()
  const ability = useContext(AbilityContext)
  const auth = useAuth()
  const currentURL = router.asPath
  const { id } = router.query
  const { direction, subNavCollapsed, verticalNavToggleType } = settings

  // ** Accordion menu group open toggle
  const toggleActiveGroup = (item: NavGroup, parent: NavGroup | undefined) => {
    let openGroup = groupActive

    // ** If Group is already open and clicked, close the group
    if (openGroup.includes(item.title)) {
      openGroup.splice(openGroup.indexOf(item.title), 1)

      // If clicked Group has open group children, Also remove those children to close those groups
      if (item.children) {
        removeChildren(item.children, openGroup, currentActiveGroup)
      }
    } else if (parent) {
      // ** If Group clicked is the child of an open group, first remove all the open groups under that parent
      if (parent.children) {
        removeChildren(parent.children, openGroup, currentActiveGroup)
      }

      // ** After removing all the open groups under that parent, add the clicked group to open group array
      if (!openGroup.includes(item.title)) {
        openGroup.push(item.title)
      }
    } else {
      // ** If clicked on another group that is not active or open, create openGroup array from scratch

      // ** Empty Open Group array
      openGroup = []

      // ** push Current Active Group To Open Group array
      if (currentActiveGroup.every(elem => groupActive.includes(elem))) {
        openGroup.push(...currentActiveGroup)
      }

      // ** Push current clicked group item to Open Group array
      if (!openGroup.includes(item.title)) {
        openGroup.push(item.title)
      }
    }

    setGroupActive([...openGroup])
  }

  // ** Menu Group Click
  const handleGroupClick = () => {
    const isCollapsedIconRail = subNavCollapsed && !navHover && !parent

    if (isCollapsedIconRail) {
      setNavHover?.(true)

      const firstLink = findFirstVisibleNavLinkInTree(item.children ?? [], ability, auth.user)
      if (firstLink?.path) {
        const href = resolveNavPath(firstLink.path, id)
        void router.push(href)
      }

      if (!groupActive.includes(item.title)) {
        setGroupActive([...groupActive, item.title])
      }

      return
    }

    const openGroup = groupActive
    if (verticalNavToggleType === 'collapse') {
      if (openGroup.includes(item.title)) {
        openGroup.splice(openGroup.indexOf(item.title), 1)
      } else {
        openGroup.push(item.title)
      }
      setGroupActive([...openGroup])
    } else {
      toggleActiveGroup(item, parent)
    }
  }

  useEffect(() => {
    if (hasSubActiveChild(item, currentURL, id as string)) {
      if (!groupActive.includes(item.title)) groupActive.push(item.title)
    } else {
      const index = groupActive.indexOf(item.title)
      if (index > -1) groupActive.splice(index, 1)
    }
    setGroupActive([...groupActive])
    setCurrentActiveGroup([...groupActive])

    // Empty Active Group When Menu is collapsed and not hovered, to fix issue route change
    if (subNavCollapsed && !navHover) {
      setGroupActive([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.asPath])

  useEffect(() => {
    if (subNavCollapsed && !navHover) {
      setGroupActive([])
    }

    if ((subNavCollapsed && navHover) || (groupActive.length === 0 && !subNavCollapsed)) {
      setGroupActive([...currentActiveGroup])
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subNavCollapsed, navHover])

  useEffect(() => {
    if (groupActive.length === 0 && !subNavCollapsed) {
      setGroupActive([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navHover])

  const menuGroupCollapsedStyles = subNavCollapsed && !navHover ? { opacity: 0 } : { opacity: 1 }

  return (
    <CanViewNavGroup navGroup={item}>
      <Fragment>
        <ListItem
          disablePadding
          className={'nav-group'}
          onClick={handleGroupClick}
          sx={{
            mt: 1,
            px: '0 !important',
            flexDirection: 'column',
            '& .MuiListItemButton-root:hover': {
              backgroundColor: 'grey.800'
            },
            '& .menu-item-meta > svg, & .menu-item-meta svg': {
              color: 'common.white'
            }
          }}
        >
          <Tooltip
            title={item.title}
            placement='right'
            enterDelay={400}
            disableHoverListener={!(subNavCollapsed && !navHover)}
          >
            <ListItemButton
              className={
                subNavCollapsed
                  ? ''
                  : clsx({
                      'Mui-selected': groupActive.includes(item.title) || currentActiveGroup.includes(item.title)
                    })
              }
              sx={{
                py: 2,
                mx: 3.5,
                borderRadius: 1,
                width: theme => `calc(100% - ${theme.spacing(3.5 * 2)})`,
                transition: 'padding-left .25s ease-in-out, padding-right .25s ease-in-out',
                px: subNavCollapsed && !navHover ? (collapsedNavWidth - navigationBorderWidth - 22 - 28) / 8 : 4,
                '&:hover': {
                  backgroundColor: 'action.hover'
                },
                '& .MuiTypography-root, & :not(.menu-item-meta) > svg': {
                  color: 'common.white'
                },
                '&.Mui-selected': {
                  backgroundColor: 'grey.800',
                  '&:hover': {
                    backgroundColor: 'grey.800'
                  },
                  '& .MuiTypography-root': {
                    backgroundColor: 'action.selected'
                  },
                  '& :not(.menu-item-meta) > svg': {
                    color: 'common.white'
                  },
                  '& .menu-item-meta > svg, & .menu-item-meta svg': {
                    color: 'common.white'
                  },
                  '&.Mui-focusVisible': {
                    backgroundColor: 'action.active',
                    '&:hover': {
                      backgroundColor: 'action.active'
                    }
                  }
                }
              }}
            >
              <ListItemIcon
                sx={{
                  transition: 'margin .25s ease-in-out',
                  ...(parent && subNavCollapsed && !navHover ? {} : { mr: 2 }),
                  ...(subNavCollapsed && !navHover ? { mr: 0 } : {}), // this condition should come after (parent && navCollapsed && !navHover) condition for proper styling
                  ...(parent && item.children ? { ml: 1.5, mr: 3.5 } : {})
                }}
              ></ListItemIcon>
              <MenuItemTextWrapper sx={{ ...menuGroupCollapsedStyles, ...(isSubToSub ? { ml: 2 } : {}) }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <NavTruncationTooltip title={item.title} variant='body1' placement='right' sx={{ color: 'inherit' }}>
                    <Translations text={item.title} />
                  </NavTruncationTooltip>
                </Box>
                <Box
                  className='menu-item-meta'
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0,
                    '& svg': {
                      color: 'text.disabled',
                      transition: 'transform .25s ease-in-out',
                      ...(groupActive.includes(item.title) && {
                        transform: direction === 'ltr' ? 'rotate(90deg)' : 'rotate(-90deg)'
                      })
                    }
                  }}
                >
                  {item.badgeContent ? (
                    <Chip
                      size='small'
                      label={item.badgeContent}
                      color={item.badgeColor || 'primary'}
                      sx={{
                        mr: 2,
                        height: 22,
                        minWidth: 22,
                        '& .MuiChip-label': { px: 1.5, textTransform: 'capitalize' }
                      }}
                    />
                  ) : null}
                  <Icon
                    fontSize='1.125rem'
                    icon={direction === 'ltr' ? 'tabler:chevron-right' : 'tabler:chevron-left'}
                  />
                </Box>
              </MenuItemTextWrapper>
            </ListItemButton>
          </Tooltip>
          <Collapse
            className='menu-group'
            component='ul'
            onClick={e => e.stopPropagation()}
            in={groupActive.includes(item.title)}
            sx={{
              pl: 0,
              width: '100%',
              ...menuGroupCollapsedStyles,
              transition: 'all 0.25s ease-in-out'
            }}
          >
            <SubVerticalSubNavItems
              {...props}
              parent={item}
              navVisible={navVisible}
              verticalNavItems={item.children}
              isSubToSub={parent && item.children ? item : undefined}
            />
          </Collapse>
        </ListItem>
      </Fragment>
    </CanViewNavGroup>
  )
}

export default SubVerticalNavGroup
